/**
 * White-Label Setup Page - Admin UI for configuring new client instances
 * Only accessible to master admin, not tenant users
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Settings, Palette, Globe, Lock, Database, Sparkles,
  Save, RefreshCw, CheckCircle, AlertCircle, ArrowLeft,
  Download, Copy, Eye, EyeOff, Smartphone, Monitor, 
  Tags, QrCode, Upload as UploadIcon, X, Users, Plus,
  Trash2, Building2, Activity, Files, ShieldAlert
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhiteLabelSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewPage, setPreviewPage] = useState('landing');
  const [activeSection, setActiveSection] = useState('config'); // 'config' or 'tenants'
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [showNewTenantForm, setShowNewTenantForm] = useState(false);
  const [cloningFrom, setCloningFrom] = useState(null);
  const [newTenant, setNewTenant] = useState({
    tenant_id: '',
    display_name: '',
    domain: '',
    isolation_mode: 'collection',
    branding: { primary_color: '#6366F1' },
    is_active: true
  });

  // Check if user has access to white-label setup
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/context`);
        if (!response.data.features?.white_label) {
          setAccessDenied(true);
        }
      } catch (error) {
        console.error('Failed to check access:', error);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, []);
  
  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const response = await axios.get(`${API}/admin/tenants`);
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setLoadingTenants(false);
    }
  }, []);
  
  const cloneTenant = (tenant) => {
    setCloningFrom(tenant.tenant_id);
    setNewTenant({
      tenant_id: '',  // Require new ID
      display_name: `${tenant.display_name} (Copy)`,
      domain: '',  // Require new domain
      isolation_mode: tenant.isolation_mode,
      branding: { ...tenant.branding },
      storage_config: tenant.storage_config ? { ...tenant.storage_config } : {},
      is_active: true
    });
    setShowNewTenantForm(true);
  };
  
  useEffect(() => {
    if (activeSection === 'tenants') {
      loadTenants();
    }
  }, [activeSection, loadTenants]);
  
  const createTenant = async () => {
    if (!newTenant.tenant_id || !newTenant.display_name) {
      toast.error('Tenant ID and Display Name are required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/tenants`, newTenant);
      toast.success(`Tenant "${newTenant.display_name}" ${cloningFrom ? 'cloned' : 'created'}`);
      setShowNewTenantForm(false);
      setCloningFrom(null);
      setNewTenant({
        tenant_id: '',
        display_name: '',
        domain: '',
        isolation_mode: 'collection',
        branding: { primary_color: '#6366F1' },
        is_active: true
      });
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    }
  };
  
  const deleteTenant = async (tenantId) => {
    if (!window.confirm(`Delete tenant "${tenantId}"? The data will remain intact.`)) return;
    
    try {
      await axios.delete(`${API}/admin/tenants/${tenantId}`);
      toast.success('Tenant deleted');
      loadTenants();
    } catch (error) {
      toast.error('Failed to delete tenant');
    }
  };
  
  const [config, setConfig] = useState({
    client_name: '',
    domain: '',
    admin_password: '',
    branding: {
      primary_color: '#6366F1',
      description: 'Personalized NFC tag memories',
      remove_emergent_badge: false
    },
    storage: {
      use_shared_bucket: true,
      r2_bucket_name: '',
      r2_account_id: '',
      r2_access_key_id: '',
      r2_secret_access_key: ''
    }
  });

  const [validation, setValidation] = useState({
    client_name: null,
    domain: null,
    admin_password: null
  });

  // Validate fields
  const validateField = (field, value) => {
    switch (field) {
      case 'client_name':
        return value.length >= 2 ? 'valid' : 'Client name must be at least 2 characters';
      case 'domain':
        if (!value) return 'Domain is required';
        if (!value.startsWith('https://')) return 'Domain must start with https://';
        return 'valid';
      case 'admin_password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return 'valid';
      default:
        return null;
    }
  };

  const handleChange = (section, field, value) => {
    if (section) {
      setConfig(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Validate main fields
      if (['client_name', 'domain', 'admin_password'].includes(field)) {
        setValidation(prev => ({
          ...prev,
          [field]: validateField(field, value)
        }));
      }
    }
  };

  const isValid = () => {
    return (
      validation.client_name === 'valid' &&
      validation.domain === 'valid' &&
      validation.admin_password === 'valid'
    );
  };

  const downloadConfig = () => {
    const configJson = JSON.stringify(config, null, 2);
    const blob = new Blob([configJson], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${config.client_name.toLowerCase().replace(/\s+/g, '-') || 'client'}_config.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Configuration downloaded');
  };

  const copyConfig = () => {
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    toast.success('Configuration copied to clipboard');
  };

  const applyConfig = async () => {
    if (!isValid()) {
      toast.error('Please fix validation errors first');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/whitelabel/apply`, config);
      if (response.data.success) {
        toast.success('White-label configuration applied successfully!');
        toast.info('Restart the backend to apply all changes');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply configuration');
    } finally {
      setLoading(false);
    }
  };

  const InputField = ({ label, value, onChange, type = 'text', placeholder, error, icon: Icon }) => (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        {label}
      </label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-secondary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 transition-all ${
            error && error !== 'valid' 
              ? 'ring-2 ring-destructive focus:ring-destructive' 
              : 'focus:ring-primary'
          }`}
        />
        {error === 'valid' && (
          <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
        )}
      </div>
      {error && error !== 'valid' && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );

  // Show loading while checking access
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show access denied for tenant users
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            White-Label setup is only available to master administrators. 
            If you believe this is an error, please contact your system administrator.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="w-8 h-8 text-primary" />
                <h1 className="text-3xl font-bold">White-Label Setup</h1>
              </div>
              {/* Section Toggle */}
              <div className="flex bg-secondary rounded-full p-1">
                <button
                  onClick={() => setActiveSection('config')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeSection === 'config' ? 'bg-primary text-primary-foreground' : ''
                  }`}
                >
                  <Palette className="w-4 h-4 inline mr-2" />
                  Configuration
                </button>
                <button
                  onClick={() => setActiveSection('tenants')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeSection === 'tenants' ? 'bg-primary text-primary-foreground' : ''
                  }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Tenants
                </button>
              </div>
            </div>
            <p className="text-muted-foreground mt-2">
              {activeSection === 'config' 
                ? 'Configure a new client instance with custom branding and settings'
                : 'Manage multi-tenant database isolation for enterprise clients'}
            </p>
          </div>

          {/* Tenants Section */}
          {activeSection === 'tenants' && (
            <div className="space-y-6">
              {/* Tenant Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  Multi-Tenant Management
                </h2>
                <button
                  onClick={() => setShowNewTenantForm(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add Tenant
                </button>
              </div>

              {/* New Tenant Form */}
              {showNewTenantForm && (
                <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      {cloningFrom ? `Clone Tenant: ${cloningFrom}` : 'Create New Tenant'}
                    </h3>
                    {cloningFrom && (
                      <span className="flex items-center gap-1 text-sm text-primary bg-primary/10 px-3 py-1 rounded-full">
                        <Files className="w-4 h-4" />
                        Cloning configuration
                      </span>
                    )}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <InputField
                      label="Tenant ID *"
                      value={newTenant.tenant_id}
                      onChange={(v) => setNewTenant(prev => ({ ...prev, tenant_id: v.toLowerCase().replace(/\s/g, '-') }))}
                      placeholder="my-client"
                      error={cloningFrom && !newTenant.tenant_id ? 'Required for clone' : undefined}
                    />
                    <InputField
                      label="Display Name"
                      value={newTenant.display_name}
                      onChange={(v) => setNewTenant(prev => ({ ...prev, display_name: v }))}
                      placeholder="My Client Inc"
                    />
                    <InputField
                      label="Domain"
                      value={newTenant.domain}
                      onChange={(v) => setNewTenant(prev => ({ ...prev, domain: v }))}
                      placeholder="https://client.example.com"
                    />
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Isolation Mode</label>
                      <select
                        value={newTenant.isolation_mode}
                        onChange={(e) => setNewTenant(prev => ({ ...prev, isolation_mode: e.target.value }))}
                        className="w-full bg-secondary rounded-xl px-4 py-3"
                      >
                        <option value="collection">Collection-based (shared DB)</option>
                        <option value="database">Database-based (separate DB)</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        {newTenant.isolation_mode === 'database' 
                          ? 'Creates a separate database for this tenant'
                          : 'Uses prefixed collections in the main database'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={createTenant}
                      className="px-6 py-2 rounded-full bg-primary text-primary-foreground font-medium flex items-center gap-2"
                    >
                      {cloningFrom && <Files className="w-4 h-4" />}
                      {cloningFrom ? 'Clone Tenant' : 'Create Tenant'}
                    </button>
                    <button
                      onClick={() => {
                        setShowNewTenantForm(false);
                        setCloningFrom(null);
                        setNewTenant({
                          tenant_id: '',
                          display_name: '',
                          domain: '',
                          isolation_mode: 'collection',
                          branding: { primary_color: '#6366F1' },
                          is_active: true
                        });
                      }}
                      className="px-6 py-2 rounded-full bg-secondary font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Tenant List */}
              {loadingTenants ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : tenants.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No tenants configured</p>
                  <p className="text-sm">Create a tenant for multi-instance deployment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tenants.map((tenant) => (
                    <div
                      key={tenant.tenant_id}
                      className="rounded-2xl border border-border/40 bg-card/50 p-6"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: (tenant.branding?.primary_color || '#6366F1') + '20' }}
                          >
                            <Building2 className="w-6 h-6" style={{ color: tenant.branding?.primary_color || '#6366F1' }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{tenant.display_name}</h3>
                            <p className="text-sm text-muted-foreground font-mono">{tenant.tenant_id}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            tenant.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {tenant.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => cloneTenant(tenant)}
                            className="p-2 hover:bg-primary/10 text-primary rounded-full transition-colors"
                            title="Clone Tenant"
                            data-testid={`clone-tenant-${tenant.tenant_id}`}
                          >
                            <Files className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTenant(tenant.tenant_id)}
                            className="p-2 hover:bg-destructive/10 text-destructive rounded-full transition-colors"
                            title="Delete Tenant"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border/40">
                        <div>
                          <p className="text-xs text-muted-foreground">Domain</p>
                          <p className="text-sm font-medium truncate">{tenant.domain || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Isolation</p>
                          <p className="text-sm font-medium capitalize">{tenant.isolation_mode}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Database</p>
                          <p className="text-sm font-mono text-muted-foreground truncate">{tenant.db_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Created</p>
                          <p className="text-sm font-medium">
                            {new Date(tenant.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Info Box */}
              <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  About Multi-Tenant Isolation
                </h3>
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>
                    <strong>Collection-based:</strong> Tags are stored in prefixed collections (e.g., <code>client1_nfc_tags</code>) 
                    within the same database. Best for small-medium clients.
                  </p>
                  <p>
                    <strong>Database-based:</strong> Each tenant gets a separate MongoDB database 
                    (e.g., <code>nfc_tenant_client1</code>). Best for enterprise clients requiring complete isolation.
                  </p>
                  <p className="text-xs pt-2 border-t border-border/40 mt-3">
                    Tenant data can be accessed via the <code>X-Tenant-ID</code> header or subdomain routing.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Section */}
          {activeSection === 'config' && (
          <div className="space-y-8">
            {/* Basic Info */}
            <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Basic Information
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <InputField
                  label="Client Name"
                  value={config.client_name}
                  onChange={(v) => handleChange(null, 'client_name', v)}
                  placeholder="My NFC App"
                  error={validation.client_name}
                />
                <InputField
                  label="Domain"
                  value={config.domain}
                  onChange={(v) => handleChange(null, 'domain', v)}
                  placeholder="https://app.example.com"
                  error={validation.domain}
                />
              </div>
            </div>

            {/* Security */}
            <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Security
              </h2>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  Admin Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={config.admin_password}
                    onChange={(e) => handleChange(null, 'admin_password', e.target.value)}
                    placeholder="Secure password (min 8 chars)"
                    className={`w-full bg-secondary rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 transition-all ${
                      validation.admin_password && validation.admin_password !== 'valid'
                        ? 'ring-2 ring-destructive'
                        : 'focus:ring-primary'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {validation.admin_password && validation.admin_password !== 'valid' && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {validation.admin_password}
                  </p>
                )}
              </div>
            </div>

            {/* Branding */}
            <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" />
                Branding
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Primary Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={config.branding.primary_color}
                      onChange={(e) => handleChange('branding', 'primary_color', e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={config.branding.primary_color}
                      onChange={(e) => handleChange('branding', 'primary_color', e.target.value)}
                      className="flex-1 bg-secondary rounded-xl px-4 py-3 font-mono"
                    />
                    <div
                      className="w-12 h-12 rounded-lg"
                      style={{ backgroundColor: config.branding.primary_color }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <input
                    type="text"
                    value={config.branding.description}
                    onChange={(e) => handleChange('branding', 'description', e.target.value)}
                    placeholder="Personalized NFC tag memories"
                    className="w-full bg-secondary rounded-xl px-4 py-3"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.branding.remove_emergent_badge}
                    onChange={(e) => handleChange('branding', 'remove_emergent_badge', e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                  <span className="text-sm">Remove "Made with Emergent" badge</span>
                </label>
              </div>
            </div>

            {/* Storage */}
            <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                File Storage
              </h2>
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="storage"
                    checked={config.storage.use_shared_bucket}
                    onChange={() => handleChange('storage', 'use_shared_bucket', true)}
                    className="w-5 h-5"
                  />
                  <div>
                    <span className="font-medium">Shared Storage</span>
                    <p className="text-sm text-muted-foreground">Use shared R2 bucket (simpler)</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="storage"
                    checked={!config.storage.use_shared_bucket}
                    onChange={() => handleChange('storage', 'use_shared_bucket', false)}
                    className="w-5 h-5"
                  />
                  <div>
                    <span className="font-medium">Separate Storage</span>
                    <p className="text-sm text-muted-foreground">Client's own R2 bucket (more secure)</p>
                  </div>
                </label>
                
                {!config.storage.use_shared_bucket && (
                  <div className="grid gap-4 md:grid-cols-2 mt-4 pt-4 border-t border-border/40">
                    <InputField
                      label="R2 Bucket Name"
                      value={config.storage.r2_bucket_name}
                      onChange={(v) => handleChange('storage', 'r2_bucket_name', v)}
                      placeholder="client-bucket"
                    />
                    <InputField
                      label="R2 Account ID"
                      value={config.storage.r2_account_id}
                      onChange={(v) => handleChange('storage', 'r2_account_id', v)}
                      placeholder="account_id"
                    />
                    <InputField
                      label="R2 Access Key ID"
                      value={config.storage.r2_access_key_id}
                      onChange={(v) => handleChange('storage', 'r2_access_key_id', v)}
                      placeholder="access_key_id"
                    />
                    <InputField
                      label="R2 Secret Access Key"
                      value={config.storage.r2_secret_access_key}
                      onChange={(v) => handleChange('storage', 'r2_secret_access_key', v)}
                      type="password"
                      placeholder="secret_key"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 text-primary hover:bg-primary/20 font-medium transition-all"
                data-testid="preview-btn"
              >
                <Eye className="w-4 h-4" />
                Preview Branding
              </button>
              <button
                onClick={downloadConfig}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-secondary hover:bg-secondary/80 font-medium transition-all"
              >
                <Download className="w-4 h-4" />
                Download Config
              </button>
              <button
                onClick={copyConfig}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-secondary hover:bg-secondary/80 font-medium transition-all"
              >
                <Copy className="w-4 h-4" />
                Copy Config
              </button>
              <button
                onClick={applyConfig}
                disabled={!isValid() || loading}
                className="flex items-center gap-2 px-8 py-3 rounded-full bg-primary text-primary-foreground hover:opacity-90 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Apply Configuration
              </button>
            </div>

            {/* Instructions */}
            <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
              <h2 className="text-xl font-semibold mb-4">📝 Next Steps</h2>
              <ol className="space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="font-bold text-foreground">1.</span>
                  Fill in the configuration above with client details
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-foreground">2.</span>
                  Preview branding to see how it will look
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-foreground">3.</span>
                  Download or copy the config for your records
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-foreground">4.</span>
                  Click "Apply Configuration" to update the app
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-foreground">5.</span>
                  Deploy the app and link the custom domain
                </li>
              </ol>
            </div>
          </div>
          )}
        </motion.div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
          >
            {/* Preview Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-xl font-semibold">Branding Preview</h3>
                <div className="flex bg-secondary rounded-full p-1">
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      previewDevice === 'desktop' ? 'bg-white shadow-sm' : ''
                    }`}
                  >
                    <Monitor className="w-4 h-4 inline mr-1" />
                    Desktop
                  </button>
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      previewDevice === 'mobile' ? 'bg-white shadow-sm' : ''
                    }`}
                  >
                    <Smartphone className="w-4 h-4 inline mr-1" />
                    Mobile
                  </button>
                </div>
                <div className="flex bg-secondary rounded-full p-1">
                  {['landing', 'upload', 'admin'].map((page) => (
                    <button
                      key={page}
                      onClick={() => setPreviewPage(page)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${
                        previewPage === page ? 'bg-white shadow-sm' : ''
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Preview Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div
                className={`mx-auto bg-card border border-border rounded-2xl overflow-hidden shadow-lg transition-all ${
                  previewDevice === 'mobile' ? 'max-w-sm' : 'max-w-4xl'
                }`}
                style={{
                  '--preview-primary': config.branding.primary_color,
                }}
              >
                {/* Mock Browser Chrome */}
                <div className="bg-secondary/50 p-2 flex items-center gap-2 border-b border-border/50">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-background rounded-md px-3 py-1 text-xs text-muted-foreground truncate">
                    {config.domain || 'https://your-domain.com'}
                  </div>
                </div>

                {/* Landing Page Preview */}
                {previewPage === 'landing' && (
                  <div className="p-8 text-center">
                    <div 
                      className="w-20 h-20 mx-auto rounded-2xl mb-6 flex items-center justify-center"
                      style={{ backgroundColor: config.branding.primary_color + '20' }}
                    >
                      <Tags className="w-10 h-10" style={{ color: config.branding.primary_color }} />
                    </div>
                    <h1 className="text-3xl font-bold mb-3">{config.client_name || 'Your Brand'}</h1>
                    <p className="text-muted-foreground mb-8">{config.branding.description}</p>
                    <button
                      className="px-8 py-3 rounded-full text-white font-medium"
                      style={{ backgroundColor: config.branding.primary_color }}
                    >
                      Scan to View
                    </button>
                    {!config.branding.remove_emergent_badge && (
                      <div className="mt-12 text-xs text-muted-foreground">Made with Emergent</div>
                    )}
                  </div>
                )}

                {/* Upload Page Preview */}
                {previewPage === 'upload' && (
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: config.branding.primary_color + '20' }}
                      >
                        <UploadIcon className="w-5 h-5" style={{ color: config.branding.primary_color }} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">Upload Content</h2>
                        <p className="text-sm text-muted-foreground">{config.branding.description}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <label className="text-sm font-medium block mb-2">Title</label>
                        <div className="bg-background rounded-lg p-3 text-muted-foreground">Enter a title...</div>
                      </div>
                      <div className="bg-secondary/50 rounded-xl p-4">
                        <label className="text-sm font-medium block mb-2">Message</label>
                        <div className="bg-background rounded-lg p-3 h-24 text-muted-foreground">Write your message...</div>
                      </div>
                      <div className="border-2 border-dashed rounded-xl p-8 text-center">
                        <UploadIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Drop files here</p>
                      </div>
                      <button
                        className="w-full py-3 rounded-full text-white font-medium"
                        style={{ backgroundColor: config.branding.primary_color }}
                      >
                        Save Content
                      </button>
                    </div>
                  </div>
                )}

                {/* Admin Page Preview */}
                {previewPage === 'admin' && (
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <Settings className="w-6 h-6" style={{ color: config.branding.primary_color }} />
                        <h2 className="text-xl font-bold">Admin Dashboard</h2>
                      </div>
                      <button 
                        className="px-4 py-2 rounded-full text-sm"
                        style={{ backgroundColor: config.branding.primary_color + '20', color: config.branding.primary_color }}
                      >
                        <Palette className="w-4 h-4 inline mr-1" />
                        White-Label
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {['Total Tags', 'With Content', 'Views', 'Visitors'].map((stat, i) => (
                        <div key={stat} className="bg-secondary/50 rounded-xl p-4">
                          <p className="text-xs text-muted-foreground">{stat}</p>
                          <p className="text-2xl font-bold mt-1">{[31, 22, '1.1K', 171][i]}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mb-4">
                      {['All Tags', 'Batches', 'Generator'].map((tab, i) => (
                        <button
                          key={tab}
                          className={`px-4 py-2 rounded-full text-sm font-medium ${
                            i === 0 ? 'text-white' : 'bg-secondary/50'
                          }`}
                          style={i === 0 ? { backgroundColor: config.branding.primary_color } : {}}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-secondary/30 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">Tag {i}</span>
                            <QrCode className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="text-sm text-muted-foreground truncate">sample-tag-{i}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Color Legend */}
              <div className="mt-6 flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: config.branding.primary_color }} 
                  />
                  <span className="text-muted-foreground">Primary Color</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-secondary" />
                  <span className="text-muted-foreground">Secondary (Auto)</span>
                </div>
                {config.branding.remove_emergent_badge && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-muted-foreground">Badge Removed</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default WhiteLabelSetup;
